/**
 * Formula AST describing how a column is computed from other columns and
 * scalar params (filter-derived values that aren't part of row data).
 *
 *   - `col`: value comes from a row column (read by id)
 *   - `const`: literal scalar baked into the formula
 *   - `param`: scalar from `formulaParams` (e.g. weeksInPeriod)
 */
export type Formula =
  | { kind: "col"; id: string }
  | { kind: "const"; value: number }
  | { kind: "param"; id: string }
  | { kind: "sum"; terms: Formula[] }
  | { kind: "diff"; from: Formula; minus: Formula[] }
  | { kind: "ratio"; num: Formula; den: Formula }
  | { kind: "product"; factors: Formula[] }

export type Role = "addend" | "detractor" | "divisor" | "result"

/**
 * CSS-var-based bg/fg colors per role. Shared by the FormulaBar chips and the
 * inspection cellStyle so the two always agree on the palette.
 */
export const ROLE_COLORS: Record<Role, { bg: string; fg: string }> = {
  addend: {
    bg: "var(--color-badge-green-bg)",
    fg: "var(--color-badge-green-fg-text)",
  },
  detractor: {
    bg: "var(--color-badge-red-bg)",
    fg: "var(--color-badge-red-fg-text)",
  },
  divisor: {
    bg: "var(--color-badge-orange-bg)",
    fg: "var(--color-badge-orange-fg-text)",
  },
  result: {
    bg: "var(--color-badge-neutral-bg)",
    fg: "var(--color-badge-neutral-fg-text)",
  },
}

/**
 * The cell whose formula breakdown is on show. Unrelated to the design-system Inspector
 * (`design-system/pages`), which is the detail surface beside a Module's content — this
 * "inspection" is only ever a cell's formula.
 */
export type InspectedCell = { rowId: string; colId: string }

export const col = (id: string): Formula => ({ kind: "col", id })
export const param = (id: string): Formula => ({ kind: "param", id })

/** Type-narrowed sign flip — avoids `-sign as 1 | -1` casts at call sites. */
export const flipSign = (sign: 1 | -1): 1 | -1 => (sign === 1 ? -1 : 1)

/**
 * Computes the contextual note rendered on the right side of the FormulaBar,
 * explaining post-formula adjustments (e.g. "Adjusted to 144 to meet MOQ").
 * Return `null` / `undefined` / `""` to omit.
 */
export type RelationalFormulaNoteFn = (args: {
  row: Record<string, unknown>
  params: Record<string, number>
  /** Value displayed in the bar's `≈` chip — the inspected column's cell value. */
  result: number
}) => string | null | undefined

export type RelationalFormulaValueFn = (args: {
  row: Record<string, unknown>
  params: Record<string, number>
}) => number

/**
 * What a colDef declares under `context` to take part in DataTableV2's inspection: set
 * `relationalFormula` and the column's cell opens the formula bar and highlights related cells.
 * Lives in `context` because ag-grid flags unknown top-level colDef properties.
 */
export interface RelationalFormulaContext {
  relationalFormula?: Formula
  /** Optional contextual note rendered on the right side of the FormulaBar. */
  relationalFormulaNote?: RelationalFormulaNoteFn
  relationalFormulaValue?: RelationalFormulaValueFn
}

export function relationalFormulaContext(def: {
  context?: unknown
}): RelationalFormulaContext | undefined {
  return def.context as RelationalFormulaContext | undefined
}
